
import Vue from 'vue';
import { Location } from 'vue-router';

import AddFloorMapModal from '@/components/modal/AddFloorMapModal.vue';
import AreaEditModal from '@/components/modal/AreaEditModal.vue';
import ConfirmActionModal from '@/components/modal/ConfirmActionModal.vue';
import DeviceConfigurationModal from '@/components/modal/DeviceConfigurationModal.vue';
import GatewayList from '@/components/configuration/GatewayList.vue';
import TreeView from '@/components/dashboard/TreeView.vue';
import { ExpansionCache } from '@/lib/treeExpansionCache';
import { Controller } from '@/interfaces/controllers';
import {
	AddMapData,
	Device,
	DeviceAreaEdit,
	DeviceArea,
	DeviceEdit,
	Floor,
} from '@/interfaces/locations';
import { LocationTree } from '@/interfaces/locationTree';
import { LightingEdit, Room, Zone } from '@/interfaces/lighting';
import permissions from '@/lib/permissions';
import { Role } from '@/interfaces/types';
import { AxiosError } from 'axios';

interface SiteConfigData {
	deviceCreationParams: {
		deviceGroup?: string;
		parent?: Device | Floor;
	};
	floorLuminaires: Device[];
	deleteLocation: DeleteObject | null;
	poller: number | null;
}

interface DeleteObject {
	id: number;
	type: string;
	name: string;
}

export default Vue.extend({
	components: {
		AddFloorMapModal,
		AreaEditModal,
		ConfirmActionModal,
		DeviceConfigurationModal,
		GatewayList,
		TreeView,
	},

	data(): SiteConfigData {
		return {
			deviceCreationParams: {},
			deleteLocation: null,
			floorLuminaires: [],
			poller: null,
		};
	},

	watch: {
		deleteLocation: {
			deep: true,
			handler(val) {
				this.deleteLocation = val;
			},
		},
	},

	computed: {
		expansionCache(): ExpansionCache {
			return new ExpansionCache(this.$route.query.expanded);
		},
		treeConfigData(): LocationTree[] {
			if (this.$store.getters['locations/floors/getFloors'].length !== 0) {
				return this.$store.getters['locations/floors/getSiteFloors'](
					this.$store.getters['tenancy/sites/getSelectedSite'].id
				)
					.sort((floorA: Floor, floorB: Floor) => {
						const nameA = floorA.name.toUpperCase();
						const nameB = floorB.name.toUpperCase();
						if (nameA < nameB) {
							return -1;
						}
						if (nameA > nameB) {
							return 1;
						}
						return 0;
					})
					.map((floor: Floor) => {
						const nodes: LocationTree[] = this.$store.getters['locations/rooms/getFloorRooms'](
							floor.id
						)
							.sort((roomA: Room, roomB: Room) => {
								const nameA = roomA.name.toUpperCase();
								const nameB = roomB.name.toUpperCase();
								if (nameA < nameB) {
									return -1;
								}
								if (nameA > nameB) {
									return 1;
								}
								return 0;
							})
							.map(this.getFloorChild);
						return {
							...floor,
							expanded: this.expansionCache.contains({ category: 'Edit floor', id: floor.id }),
							buttons: [
								{
									buttonName: floor.image
										? this.$t('Edit map').toString()
										: this.$t('Add map').toString(),
									buttonColor: 'info',
									category: 'map',
									disable: this.userCanWriteAreas && this.userCanWriteRooms,
								},
								{
									buttonName: this.$t('Add room').toString(),
									buttonColor: 'info',
									category: 'rooms',
									disable: this.userCanWriteAreas && this.userCanWriteRooms,
								},
								{
									buttonName: this.$t('Edit floor').toString(),
									buttonColor: 'info',
									category: 'editFloor',
									disable: this.userCanWriteAreas && this.userCanWriteRooms,
								},
								{
									buttonName: this.$t('Delete floor').toString(),
									buttonColor: 'danger',
									category: 'deleteFloor',
									disable: this.userCanWriteAreas && this.userCanWriteRooms,
								},
							],
							category: 'Edit floor',
							nodes: nodes,
						};
					});
			} else {
				return [];
			}
		},

		gateways(): Device[] {
			return this.$store.getters['devices/gateways/getGateways'];
		},

		userCanReadAreas(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'areas',
				'read',
			]);
		},

		userCanReadRooms(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'rooms',
				'read',
			]);
		},

		userCanReadGateways(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'gateways',
				'read',
			]);
		},

		userCanWriteAreas(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'areas',
				'write',
			]);
		},

		userCanWriteRooms(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'rooms',
				'write',
			]);
		},

		userCanWriteGateways(): boolean {
			const role: Role = this.$store.getters['usermanagement/user/getRole'];

			return (
				permissions.hasPermission(role, ['gateways', 'write']) &&
				permissions.hasPermission(role, ['controllers', 'write']) &&
				permissions.hasPermission(role, ['rules', 'write'])
			);
		},

		controllers(): Controller[] {
			const ids = this.gateways.filter((g) => g.fk_controller !== null).map((g) => g.fk_controller);
			return this.$store.getters['controllers/controllers/getControllers'].filter((c: Controller) =>
				ids.includes(c.id)
			);
		},

		rooms(): Room[] {
			return this.$store.getters['locations/rooms/getRooms'];
		},

		zones(): Zone[] {
			return this.$store.getters['locations/zones/getZones'];
		},

		floors(): Floor[] {
			return this.$store.getters['locations/floors/getFloors'];
		},

		deviceAreas(): DeviceArea[] {
			return this.$store.getters['locations/deviceAreas/getDeviceAreas'];
		},
	},

	methods: {
		onTreeExpanded(opened: boolean, category: string, id: number) {
			if (opened) {
				this.expansionCache.add({ category, id });
			} else {
				this.expansionCache.remove({ category, id });
			}
			this.$router.replace({
				...this.$route,
				query: { expanded: this.expansionCache.queryParams },
			} as Location);
		},
		navigation(id: string) {
			this.$router.push({ name: 'Zone', params: { zoneId: id } });
		},

		getFloorChild(room: Room): LocationTree {
			const nodes: LocationTree[] = this.$store.getters['locations/zones/getRoomZones'](room.id)
				.sort((zoneA: Zone, zoneB: Zone) => {
					const nameA = zoneA.name.toUpperCase();
					const nameB = zoneB.name.toUpperCase();
					if (nameA < nameB) {
						return -1;
					}
					if (nameA > nameB) {
						return 1;
					}
					return 0;
				})
				.map(this.getRoomChild);

			return {
				...room,
				expanded: this.expansionCache.contains({ category: 'Edit Room', id: room.id }),
				buttons: [
					{
						buttonName: this.$t('Add zone').toString(),
						buttonColor: 'info',
						category: 'zones',
						disable: this.userCanWriteRooms,
					},
					{
						buttonName: this.$t('Edit room').toString(),
						buttonColor: 'info',
						category: 'editRoom',
						disable: this.userCanWriteRooms,
					},
					{
						buttonName: this.$t('Delete room').toString(),
						buttonColor: 'danger',
						category: 'deleteRoom',
						disable: this.userCanWriteRooms,
					},
				],
				category: 'Edit Room',
				nodes: nodes,
			};
		},

		getRoomChild(zone: Zone): LocationTree {
			return {
				...zone,
				expanded: this.expansionCache.contains({ category: 'Edit zone', id: zone.id }),
				buttons: [
					{
						buttonName: this.$t('Edit zone').toString(),
						buttonColor: 'info',
						category: 'editZone',
						disable: this.userCanWriteRooms,
					},
					{
						buttonName: this.$t('Delete zone').toString(),
						buttonColor: 'danger',
						category: 'deleteZone',
						disable: this.userCanWriteRooms,
					},
				],
				category: 'Edit zone',
				changeNavigation: String(zone.id),
			};
		},

		addFloorModal() {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.areaEditModal as any).showModal();
		},

		addDataModal(category: string, deviceName: string, parentId: number) {
			this.setModalParams(parentId, category);
			let location: DeleteObject;
			switch (category) {
				case 'map':
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.addFloorMapModal as any).show();
					break;
				case 'rooms':
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.deviceConfigurationModal as any).show();
					break;
				case 'zones':
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.deviceConfigurationModal as any).show();
					break;
				case 'editFloor':
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.areaEditModal as any).showModal(parentId);
					break;
				case 'deleteFloor':
					location = this.$store.getters['locations/floors/getFloor'](parentId);
					this.deleteLocation = { id: parentId, name: location.name, type: 'floors' };
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.deleteModal as any).show();
					break;
				case 'editRoom':
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.deviceConfigurationModal as any).show(parentId);
					break;
				case 'deleteRoom':
					location = this.$store.getters['locations/rooms/getRoom'](parentId);
					this.deleteLocation = { id: parentId, name: location.name, type: 'rooms' };
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.deleteModal as any).show();
					break;
				case 'editZone':
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.deviceConfigurationModal as any).show(parentId);
					break;
				case 'deleteZone':
					location = this.$store.getters['locations/zones/getZone'](parentId);
					this.deleteLocation = { id: parentId, name: location.name, type: 'zones' };
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.deleteModal as any).show();
					break;
				default:
					throw new Error(`Unkown entity level ${category}`);
			}
		},

		setModalParams(parentId: number, category: string) {
			this.deviceCreationParams.deviceGroup = category;
			let parent: Device | undefined;
			let room: Room;
			let zone: Zone;

			let luminaires: Device[] = [];
			switch (category) {
				case 'map':
					parent = this.$store.getters['locations/floors/getFloor'](parentId);
					luminaires = this.$store.getters['devices/luminaires/getLuminaires']
						.filter((l: Device) => l.fk_area === parent?.id)
						.filter((l: Device) => l.local_latitude !== null && l.local_longitude !== null);
					break;
				case 'rooms':
					parent = this.$store.getters['locations/floors/getFloor'](parentId);
					break;
				case 'zones':
					parent = this.$store.getters['locations/rooms/getRoom'](parentId);
					break;
				case 'editRoom':
					room = this.$store.getters['locations/rooms/getRoom'](parentId);
					parent = this.$store.getters['locations/floors/getFloor'](room.fk_area);
					this.deviceCreationParams.deviceGroup = 'rooms';
					break;
				case 'editZone':
					zone = this.$store.getters['locations/zones/getZone'](parentId);
					parent = this.$store.getters['locations/rooms/getRoom'](zone.fk_room);
					this.deviceCreationParams.deviceGroup = 'zones';
					break;
				default:
					parent = undefined;
					break;
			}
			this.deviceCreationParams.parent = parent;
			this.floorLuminaires = luminaires;
		},

		async newDevice(storePath: string, newDevice: LightingEdit): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch(storePath, { payload: newDevice });

				if (storePath.endsWith('addRoom') || storePath.endsWith('addZone')) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.deviceConfigurationModal as any).hide();
				} else {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.areaEditModal as any).hide();
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-new-device',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async changedDevice(storePath: string, newDevice: LightingEdit, id: number): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch(storePath, { id: id, payload: newDevice });

				if (storePath.endsWith('modifyRoom') || storePath.endsWith('modifyZone')) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.deviceConfigurationModal as any).hide();
				} else {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.areaEditModal as any).hide();
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-change-device',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async addGateway(newGateway: DeviceEdit, newDeviceArea?: DeviceAreaEdit) {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				const result = await this.$store.dispatch('devices/gateways/addGateway', {
					payload: newGateway,
				});
				if (newDeviceArea && result) {
					newDeviceArea.fk_device = result.id;
					await this.$store.dispatch('locations/deviceAreas/addDeviceArea', {
						payload: newDeviceArea,
					});
					await this.$store.dispatch('devices/gateways/fetchGateway', { id: result.id });
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-add-gateway',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async modifyGateway(gatewayId: number, newGateway: DeviceEdit, newDeviceArea?: DeviceAreaEdit) {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				const result = await this.$store.dispatch('devices/gateways/modifyGateway', {
					id: gatewayId,
					payload: newGateway,
				});
				if (result) {
					const existingDeviceArea = this.deviceAreas.find((dA) => dA.fk_device === result.id);
					if (newDeviceArea) {
						if (existingDeviceArea) {
							await this.$store.dispatch('locations/deviceAreas/modifyDeviceArea', {
								id: existingDeviceArea.id,
								payload: newDeviceArea,
							});
						} else {
							await this.$store.dispatch('locations/deviceAreas/addDeviceArea', {
								payload: newDeviceArea,
							});
						}
						await this.$store.dispatch('devices/gateways/fetchGateway', { id: result.id });
					} else {
						if (existingDeviceArea) {
							await this.$store.dispatch('locations/deviceAreas/deleteDeviceArea', {
								id: existingDeviceArea.id,
							});
							await this.$store.dispatch('devices/gateways/fetchGateway', { id: result.id });
						}
					}
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-modify-gateway',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		navigationGateway(id: string): void {
			this.$router.push({ name: 'Gateway', params: { gatewayId: id } });
		},

		async decommissionGateway(gateway: Device): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch('devices/gateways/deleteGateway', {
					id: gateway.id,
				});
				if (gateway.fk_controller !== null) {
					await this.$store.dispatch('controllers/controllers/deleteController', {
						id: gateway.fk_controller,
					});
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-decommission-gateway',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async confirmMap(mapData: AddMapData): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch('locations/floors/modifyFloor', {
					id: mapData.id,
					payload: {
						image_bounds: mapData.bounds,
						image: mapData.map,
					},
				});

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(this.$refs.addFloorMapModal as any).hide();
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-confirm-map',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async confirm(): Promise<void> {
			if (this.deleteLocation) {
				switch (this.deleteLocation.type) {
					case 'floors':
						this.deleteFloor(this.deleteLocation.id);
						break;
					case 'rooms':
						this.deleteRoom(this.deleteLocation.id);
						break;
					case 'zones':
						this.deleteZone(this.deleteLocation.id);
						break;
				}
			}
			this.closeDeleteModal();
		},

		closeDeleteModal(): void {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.deleteModal as any).hide();
		},

		async deleteZone(zoneId: number): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch('locations/zones/modifyZone', {
					id: zoneId,
					payload: { devices: [] },
				});
				await this.$store.dispatch('locations/zones/deleteZone', { id: zoneId });
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-delete-zone',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('activities/rules/fetchRules');
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async deleteRoom(roomId: number): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				const roomZoneIds = this.$store.getters['locations/zones/getRoomZones'](roomId).map(
					(zone: Zone) => zone.id
				);
				for (const zoneId of roomZoneIds) {
					await this.deleteZone(zoneId);
				}
				await this.$store.dispatch('locations/rooms/deleteRoom', { id: roomId });
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-delete-room',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async deleteFloor(floorId: number): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				const floorRoomIds = this.$store.getters['locations/rooms/getFloorRooms'](floorId).map(
					(room: Room) => room.id
				);
				for (const roomId of floorRoomIds) {
					await this.deleteRoom(roomId);
				}

				await this.$store.dispatch('locations/floors/deleteFloor', { id: floorId });
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-delete-floor',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},
	},

	mounted() {
		this.poller = window.setInterval(
			() => this.$store.dispatch('controllers/controllers/fetchControllers'),
			10000
		);
	},
	destroyed() {
		if (this.poller !== null) {
			clearInterval(this.poller);
			this.poller = null;
		}
	},
});
